import { useEffect, useId, useMemo, useState } from 'react'

import { api, can, type RiskCell, type RiskMatrixView, type RiskRegRow, type RiskSink, codeWords, readIf } from '../lib/api'
import { Count, Icon, Skeleton } from '../components/ui'
import { PageTabList, PageTabPanel, Status, type PageTab } from '../components/kit'
import { ListCount } from '../components/DataTable'
import { findingSourceWord } from '../components/VendorFindings'

// The risk matrix and the register, one at a time.
//
// Every live finding sits on one cell: likelihood from its severity, impact
// from the vendor's inherent tier. The cells are coloured by the tenant's
// own thresholds and the appetite line is drawn, exactly as FOCTTA Core
// draws its matrix, so the same risk shows in the same place in both.
//
// The register used to sit under the matrix, 700 px down a page nine screens
// tall. They are tabs now (10 Oct 2026). Choosing a cell opens the register
// on that cell, which is the reason anybody presses one.

type RiskTab = 'matrix' | 'register' | 'others' | 'types'
// The register opens on the rows that matter most (it is sorted by severity)
// and says how many more there are.
const FIRST_ROWS = 10

const SEVERITY_WORD: Record<string, string> = { CRITICAL: 'Critical', HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' }
const STATUS_WORD: Record<string, string> = { OPEN: 'Open', IN_PROGRESS: 'In progress', ACCEPTED: 'Accepted' }
const TIER_WORD: Record<number, string> = { 4: 'Tier 1 Critical', 3: 'Tier 2 High', 2: 'Tier 3 Moderate', 1: 'Tier 4 Low' }

function typeWord(code: string): string {
  return codeWords(code)
}

function dateWord(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function Risk() {
  const [view, setView] = useState<RiskMatrixView | null>(null)
  const [error, setError] = useState<string>('')
  const [cell, setCell] = useState<string | null>(null)
  const [severity, setSeverity] = useState<string>('')
  const [q, setQ] = useState('')
  const [tab, setTab] = useState<RiskTab>('matrix')
  const tabsId = useId()
  const [allRows, setAllRows] = useState(false)
  // Choosing a cell takes the cell off the page. The keyboard goes with the
  // reader to the tab that opened, not to the top of the document.
  const [focusTab, setFocusTab] = useState<RiskTab | null>(null)
  useEffect(() => {
    if (!focusTab) return
    document.getElementById(tabsId + focusTab)?.focus()
    setFocusTab(null)
  }, [focusTab, tabsId])
  const pickCell = (key: string | null) => {
    setCell(key)
    if (key) {
      setTab('register')
      setFocusTab('register')
    }
  }
  // Where these risks go next. Read here rather than only in Settings,
  // because "is this reaching the group risk register?" is a question asked
  // in front of the matrix, not in front of a settings page.
  const [sinks, setSinks] = useState<RiskSink[] | null>(null)
  useEffect(() => {
    readIf('config.read', () => api.riskSinks(), { riskSinks: [] as RiskSink[], note: '' })
      .then((r) => setSinks(r.riskSinks))
      .catch(() => setSinks([]))
  }, [])

  useEffect(() => {
    api
      .riskMatrixView()
      .then(setView)
      .catch((e) => setError((e as Error).message ?? String(e)))
  }, [])

  const cfg = view?.riskMatrix
  const grid = useMemo(() => {
    if (!view) return []
    const byKey = new Map(view.cells.map((c) => [c.likelihood + 'x' + c.impact, c]))
    const rows: RiskCell[][] = []
    for (let l = cfg!.matrixSize; l >= 1; l--) {
      const row: RiskCell[] = []
      for (let i = 1; i <= cfg!.matrixSize; i++) row.push(byKey.get(l + 'x' + i)!)
      rows.push(row)
    }
    return rows
  }, [view, cfg])

  const rows = useMemo(() => {
    if (!view) return []
    const needle = q.trim().toLowerCase()
    return view.register.rows.filter(
      (r) =>
        (!cell || r.cell === cell) &&
        (!severity || r.severity === severity) &&
        (!needle || r.title.toLowerCase().includes(needle) || r.vendorName.toLowerCase().includes(needle) || r.vendorCode.toLowerCase().includes(needle)),
    )
  }, [view, cell, severity, q])

  if (error) {
    return (
      <div className="p1-page">
        <p className="note warn">{error}</p>
      </div>
    )
  }
  if (!view || !cfg) {
    return (
      <div className="p1-page">
        <div className="p1-head">
          <div>
            <h1>Risk</h1>
          </div>
        </div>
        <Skeleton height={320} />
      </div>
    )
  }

  const reg = view.register
  const selected = cell ? view.cells.find((c) => c.likelihood + 'x' + c.impact === cell) : null
  const filtered = cell !== null || severity !== '' || q.trim() !== ''
  const tabs: PageTab<RiskTab>[] = [
    { key: 'matrix', label: 'Matrix' },
    { key: 'register', label: 'Register', count: reg.open },
    ...(view.external.total > 0
      ? [
          {
            key: 'others' as const,
            label: 'Other registers',
            count: view.external.total,
            ...(view.external.unplaced > 0 ? { alert: view.external.unplaced + ' not on the matrix' } : {}),
          },
        ]
      : []),
    { key: 'types', label: 'By type' },
  ]
  // A filter is a request to see what matches, so it shows all of it.
  const shownRows = allRows || filtered ? rows.slice(0, 300) : rows.slice(0, FIRST_ROWS)

  return (
    <div className="p1-page risk-page">
      {/* The tabs share the title's row: under the tiles they cost the matrix
          the 70 px that kept its bottom row off a 753 px window. */}
      <div className="p1-head">
        <div>
          <h1>Risk</h1>
        </div>
        <PageTabList id={tabsId} label="Risk" tabs={tabs} value={tab} onChange={setTab} inline />
      </div>

      <div className="p1-tiles stagger">
        <a className="p1-tile" href="#/risk" onClick={() => { setCell(null); setSeverity(''); setTab('register') }}>
          <span className="n"><Count value={reg.open} /></span>
          <span className="l">Open risks</span>
        </a>
        <a className={'p1-tile' + (reg.aboveAppetite > 0 ? ' hot' : '')} href="#/risk" onClick={() => { setSeverity(''); setTab('matrix') }}>
          <span className="n"><Count value={reg.aboveAppetite} /></span>
          <span className="l">Above the appetite line</span>
        </a>
        <a className={'p1-tile' + (reg.overdue > 0 ? ' hot' : '')} href="#/findings">
          <span className="n"><Count value={reg.overdue} /></span>
          <span className="l">Overdue</span>
        </a>
        <a className="p1-tile" href="#/exceptions">
          <span className="n"><Count value={reg.accepted} /></span>
          <span className="l">Accepted with a reason</span>
        </a>
        <a className="p1-tile" href="#/vendors?unscored=1">
          <span className="n"><Count value={view.vendors.notScored} /></span>
          <span className="l">Vendors not yet scored</span>
        </a>
      </div>

      {/* Where the risks go, shown only once they go somewhere. With nothing
          connected the line said "These risks stay in TPRM, not yet reaching
          the FOCTTA risk register", which is about how the product is wired,
          not about the customer's risks (release 3.2, design rule 8).
          Connecting a register is under Settings. */}
      {sinks !== null && sinks.some((x) => x.enabled) && (
        <div className="risk-flow on">
          <span className="risk-flow-what">
            <Icon name="check" size={15} />
            {/* One line: where the risks go, then the counts. */}
            {'Copied to ' +
                sinks
                  .filter((x) => x.enabled)
                  .map((x) => x.name)
                  .join(', ') +
                ': ' +
                sinks.reduce((n, x) => n + x.sent, 0) +
                ' sent, ' +
                sinks.reduce((n, x) => n + x.pending, 0) +
                ' waiting, ' +
                sinks.reduce((n, x) => n + x.failed, 0) +
                ' failed'}
          </span>
          {can('config.read') && (
            <a className="button" href="#/settings?t=registers">
              Destinations
            </a>
          )}
        </div>
      )}

      <PageTabPanel id={tabsId} value={tab}>
      {tab === 'matrix' && (
      <section className="panel risk-matrix-panel">
        <div className="risk-matrix-head">
          <h2>
            Risk matrix <span className="muted">{cfg.matrixSize}×{cfg.matrixSize}</span>
          </h2>
          <span className="risk-legend">
            <i className="sev-low" /> Low <i className="sev-medium" /> Medium <i className="sev-high" /> High <i className="sev-critical" /> Critical
            <i className="sev-appetite" /> At or above the appetite line ({cfg.appetiteAt})
            {cfg.locked ? <Status tone="plain">Signed off</Status> : <Status tone="warn">Not signed off</Status>}
          </span>
        </div>
        <div className="risk-matrix" style={{ gridTemplateColumns: 'auto repeat(' + cfg.matrixSize + ', minmax(0, 1fr))' }}>
          <div className="rm-corner">
            <span className="rm-axis-y">Likelihood</span>
          </div>
          {cfg.impactLabels.map((label, i) => (
            <div className="rm-col-head" key={label}>
              <b>{i + 1}</b> {label}
            </div>
          ))}
          {grid.map((row, ri) => (
            <RowCells key={ri} row={row} label={cfg.likelihoodLabels[row[0]!.likelihood - 1] ?? ''} selected={cell} onPick={pickCell} />
          ))}
          <div />
          <div className="rm-axis-x" style={{ gridColumn: '2 / -1' }}>
            Impact
          </div>
        </div>
      </section>
      )}

      {tab === 'register' && (
      <section className="panel risk-register">
        <div className="risk-register-head">
          <h2>
            Register
          </h2>
          <div className="risk-filters">
            {selected && (
              <button type="button" className="chip strong" onClick={() => setCell(null)}>
                Cell {selected.likelihood}×{selected.impact} · {SEVERITY_WORD[selected.severity]} · clear
              </button>
            )}
            {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).map((s) => (
              <button
                type="button"
                key={s}
                className={'chip' + (severity === s ? ' strong' : '')}
                onClick={() => setSeverity(severity === s ? '' : s)}
              >
                {SEVERITY_WORD[s]} {reg.bySeverity[s] ?? 0}
              </button>
            ))}
            <input type="search" placeholder="Find a risk or vendor" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="empty">
            <p>
              {reg.open === 0
                ? 'No open risks, a No on a control that matters or a gate firing raises one'
                : 'Nothing matches, clear the cell or the severity'}
            </p>
            {/* A cell can hold only risks that came from another register: the
                matrix counts them ("+3 elsewhere") and this register does not
                list them. Say where they are rather than "nothing matches". */}
            {selected && selected.count === 0 && selected.external > 0 && (
              <p>
                The {selected.external} in this cell came from another register.{' '}
                <button
                  type="button"
                  className="linkish"
                  onClick={() => {
                    setTab('others')
                    setFocusTab('others')
                  }}
                >
                  Open Other registers
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="report risk-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Risk</th>
                  <th>Vendor</th>
                  <th>Type</th>
                  <th>From</th>
                  <th>Owner</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th>Cell</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((r) => (
                  <RegRow key={r.id} r={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {rows.length > 0 && (
          <ListCount shown={rows.length} total={reg.open} noun="open risks" one="open risk">
            {rows.length > shownRows.length ? ', the first ' + shownRows.length + ' listed' : ''}
          </ListCount>
        )}
        {!filtered && rows.length > FIRST_ROWS && (
          <button type="button" className="risk-more" data-testid="risk-all-rows" aria-expanded={allRows} onClick={() => setAllRows((v) => !v)}>
            {allRows ? 'Show the first ' + FIRST_ROWS : 'Show all ' + Math.min(rows.length, 300)}
          </button>
        )}
      </section>
      )}

      {/* Their register, under ours. It is a separate panel and not extra
          rows in the register above, because everything in that table has an
          owner here, a due date this product enforces and a rescore that can
          close it, and none of that is true of a risk somebody else owns. */}
      {tab === 'others' && view.external.total > 0 && (
        <section className="panel risk-external">
          <div className="risk-register-head">
            <h2>
              From your other registers
            </h2>
            <div className="risk-filters">
              {Object.entries(view.external.byOrigin).map(([origin, n]) => (
                <span className="chip" key={origin}>
                  {n} from {origin}
                </span>
              ))}
              {view.external.unplaced > 0 && (
                <a className="chip warn" href="#/risk-sources">
                  {view.external.unplaced} not on the matrix
                </a>
              )}
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Risk</th>
                  <th>From</th>
                  <th>About</th>
                  <th>Where it sits</th>
                  <th>Owner</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {view.external.rows.slice(0, 100).map((e) => (
                  <tr key={e.id}>
                    <td>
                      <strong>{e.title}</strong>
                      <span className="cell-sub mono">{e.externalId}</span>
                    </td>
                    <td>{e.originLabel}</td>
                    <td>{e.subject || e.category || ''}</td>
                    <td>
                      {e.likelihood != null && e.impact != null ? (
                        <span className="chip">
                          {e.likelihood}×{e.impact}
                        </span>
                      ) : (
                        <Status tone="warn" title={'They sent ' + (e.rawLikelihood || 'nothing') + ' and ' + (e.rawImpact || 'nothing')}>
                          Not placed
                        </Status>
                      )}
                    </td>
                    <td>{e.owner || ''}</td>
                    <td>{e.status || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListCount shown={Math.min(100, view.external.rows.length)} total={view.external.total} noun="risks" />
        </section>
      )}

      {tab === 'types' && (
      <section className="panel risk-types">
        <h2>By risk type</h2>
        <div className="risk-type-bars">
          {Object.entries(reg.byType)
            .sort((a, b) => b[1] - a[1])
            .map(([t, n]) => (
              <div className="rt-row" key={t}>
                <span className="rt-name">{typeWord(t)}</span>
                <span className="rt-bar">
                  <i style={{ width: Math.max(4, (n / Math.max(1, reg.open)) * 100) + '%' }} />
                </span>
                <span className="rt-n">{n}</span>
              </div>
            ))}
          {Object.keys(reg.byType).length === 0 && <p className="muted">No typed risks yet</p>}
        </div>
      </section>
      )}
      </PageTabPanel>
    </div>
  )
}

// What a cell says on hover, in one sentence, keeping the two counts apart.
function cellTitle(c: RiskCell): string {
  const mine = c.count === 0 ? 'No vendor risks here' : c.count + ' vendor risks across ' + c.vendors + ' vendors'
  if (c.external === 0) return mine
  return mine + '. ' + c.external + ' more from another register.'
}

function RowCells({ row, label, selected, onPick }: { row: RiskCell[]; label: string; selected: string | null; onPick: (k: string | null) => void }) {
  return (
    <>
      <div className="rm-row-head">
        <b>{row[0]?.likelihood}</b> {label}
      </div>
      {row.map((c) => {
        const key = c.likelihood + 'x' + c.impact
        const on = selected === key
        return (
          <button
            type="button"
            key={key}
            className={
              'rm-cell sev-' + c.severity.toLowerCase() + (c.aboveAppetite ? ' appetite' : '') + (on ? ' on' : '') +
              (c.count === 0 && c.external === 0 ? ' empty' : '')
            }
            onClick={() => onPick(on ? null : key)}
            title={cellTitle(c)}
            aria-pressed={on}
          >
            <span className="rm-count">{c.count}</span>
            {/* One line under the count, not two: the third line made every
                row of the matrix 20 px taller for the few cells that have it. */}
            <span className="rm-meta">
              <span className="rm-score">
                {c.score} · {SEVERITY_WORD[c.severity]}
              </span>
              {c.vendors > 0 && <span className="rm-vendors">{c.vendors === 1 ? '1 vendor' : c.vendors + ' vendors'}</span>}
            </span>
            {/* Theirs, marked apart. Adding the two into one number would tell
                a board that a corner is this product's problem when half of
                what is sitting there belongs to somebody else's register. */}
            {c.external > 0 && <span className="rm-external">+{c.external} elsewhere</span>}
          </button>
        )
      })}
    </>
  )
}

function RegRow({ r }: { r: RiskRegRow }) {
  return (
    <tr>
      <td>
        <span className={'chip sev-chip sev-' + r.severity.toLowerCase()}>{SEVERITY_WORD[r.severity] ?? r.severity}</span>
      </td>
      <td className="risk-title">
        {/* Two lines, the whole of it on hover: a title six lines long made
            every row twice as tall as the nine rows around it. */}
        <a href={'#/vendor/' + r.vendorId + '/full/findings'} title={r.title}>
          {r.title}
        </a>
      </td>
      <td>
        <a href={'#/vendor/' + r.vendorId}>{r.vendorName}</a>
        <span className="cell-sub"> {r.vendorCode} · {TIER_WORD[r.tier] ?? 'Tier not set'}</span>
      </td>
      <td>{typeWord(r.riskType)}</td>
      <td>{findingSourceWord(r.source)}</td>
      <td>{r.owner || <span className="muted">unassigned</span>}</td>
      <td className={r.overdue ? 'overdue' : ''}>{dateWord(r.dueOn)}</td>
      <td>{STATUS_WORD[r.status] ?? r.status}</td>
      <td>
        <span className="mono">
          {r.likelihood}×{r.impact} = {r.score}
        </span>
      </td>
    </tr>
  )
}
